'use client';

import React from 'react';
import { Zap, Award, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

const GOLD_ACCENT = '#faaf40';
const SHADOW_GOLD = 'rgba(250, 175, 64, 0.6)';

const philosophies = [
  {
    Icon: Zap,
    title: 'German-Engineered Tech',
    description: 'We use Miha Bodytec technology—medical-grade EMS equipment used by elite athletes.',
  },
  {
    Icon: Award,
    title: 'Specialist Training',
    description: 'Every 20-minute session is one-on-one with a specialist who knows your specific goals.',
  },
  {
    Icon: Sparkles,
    title: 'Hospital-Grade Hygiene',
    description: 'Clinical cleanliness with sanitized suits and full amenities provided for every session.',
  },
];

export default function PhilosophySection() {
  return (
    <section className="min-h-screen flex items-center justify-center py-8 lg:py-12 bg-gradient-to-b from-black via-[#0a0a0a] to-black text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full">
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          
          {/* --- Text Content Layer --- */}
          <div className="order-2 lg:order-1">
            <motion.h2 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-6 text-center lg:text-left leading-tight"
              style={{ color: GOLD_ACCENT, textShadow: `0 0 10px ${SHADOW_GOLD}` }}
            >
              The Aura Wellness Difference
            </motion.h2>
            
            <div className="space-y-4 sm:space-y-6">
              {philosophies.map((item, index) => (
                <motion.div 
                  key={item.title} 
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex flex-row items-center lg:items-start gap-4 p-3 rounded-xl transition-colors duration-300 hover:bg-white/5"
                >
                  <div className="flex-shrink-0">
                    <div 
                      className="flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full shadow-lg"
                      style={{ 
                        border: `1.5px solid ${GOLD_ACCENT}`, 
                        background: '#1c1c1c',
                        boxShadow: `0 0 8px ${SHADOW_GOLD}`
                      }}
                    >
                      <item.Icon className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: GOLD_ACCENT }} />
                    </div>
                  </div>

                  <div className="text-left">
                    <h3 
                      className="text-lg sm:text-xl font-bold mb-1" 
                      style={{ color: GOLD_ACCENT }}
                    >
                      {item.title}
                    </h3>
                    <p className="text-gray-400 text-sm sm:text-base leading-snug max-w-md">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* --- Image Content --- */}
          <div className="order-1 lg:order-2 w-full max-w-md lg:max-w-full mx-auto relative">
            <div 
              className="absolute -inset-4 opacity-20 blur-2xl rounded-full"
              style={{ background: GOLD_ACCENT }}
            />
            
            <motion.img
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              src="/about/philosophy.webp"
              alt="EMS Technology"
              className="relative w-full h-[300px] lg:h-[450px] object-cover rounded-2xl shadow-2xl"
              style={{
                border: `2px solid ${GOLD_ACCENT}`,
                boxShadow: `0 15px 30px -10px ${SHADOW_GOLD}`,
              }}
            />
          </div>

        </div>
      </div>
    </section>
  );
}